import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ChatStateService } from './chat-state.service';
import { AuthService } from './auth.service';
import { Message } from '../models/chat.models';
import { toCamelCase } from '../utils/case-converter';

@Injectable()
export class ChatOrchestratorService {
  private authService = inject(AuthService);

  constructor(
    private apiService: ApiService,
    private chatState: ChatStateService
  ) {}

  public async handleSendMessage(inputStr: string, currentId: string | null): Promise<void> {
    if (!inputStr.trim()) return;
 
    // Temporarily use a "new" id or the current one
    const activeSession = currentId || 'new';
 
    const userMessage: Message = { role: 'user', content: inputStr };
    this.chatState.addMessage(activeSession, userMessage);
    
    this.chatState.setLoading(activeSession, true);
    this.chatState.clearNotifications();
 
    // Add loading agent message
    this.chatState.addMessage(activeSession, {
      role: 'agent',
      data: { answer: '' },
      traces: [],
      isStreaming: true
    });
 
    try {
      // Create session if needed
      if (!currentId) {
        const newSession = await this.apiService.createSession(inputStr).toPromise();
        if (newSession) {
          currentId = newSession.id;
          // Transfer the temporary user message and loading indicator to the real session
          this.chatState.transferSessionState('new', currentId);
          this.chatState.setSessionId(currentId);
          this.chatState.addSession(newSession);
        }
      }
 
      // Stream chat response
      await this.streamChatResponse(inputStr, currentId!);

    } catch (err: any) {
      if (!currentId) {
        this.handleError(err, 'Failed to create new session');
        // Remove user message
        const msgs = this.chatState.getMessagesForSession('new');
        this.chatState.setMessages('new', msgs.slice(0, -2));
      } else {
        this.handleError(err, 'Failed to get chat response');
        // Remove loading agent message
        const msgs = this.chatState.getMessagesForSession(currentId);
        if (msgs.length > 0 && msgs[msgs.length - 1].role === 'agent' && msgs[msgs.length - 1].isStreaming) {
          this.chatState.setMessages(currentId, msgs.slice(0, -1));
        }
      }
    } finally {
      if (currentId) {
        this.chatState.setLoading(currentId, false);
      } else {
        this.chatState.setLoading('new', false);
      }
    }
  }
 
  private async streamChatResponse(message: string, id: string): Promise<void> {
    const response = await fetch(this.apiService.getChatUrl(), {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authService.accessToken}`
      },
      body: JSON.stringify({ message, sessionId: id })
    });
 
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
 
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');
 
    const decoder = new TextDecoder();
    let buffer = '';
 
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
 
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
 
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const rawEvent = JSON.parse(line);
          const event = toCamelCase(rawEvent);
 
          this.chatState.updateLastMessage(id, lastMsg => {
            if (lastMsg.role !== 'agent') return lastMsg;

            const updated = { ...lastMsg };

            if (event.type === 'trace') {
              updated.traces = [...(updated.traces || []), event.content];
            } else if (event.type === 'chunk') {
              if (!updated.data) updated.data = { answer: '' };
              updated.data.answer = (updated.data.answer || '') + event.text;
            } else if (event.type === 'final_response') {
              updated.data = event.data;
              updated.isStreaming = false;
              
              // NEW: Update session title if it's the first agent response OR title is still generic
              if (event.data?.conversationTitle) {
                const msgs = this.chatState.getMessagesForSession(id);
                // Check if current name is generic or it's the first turn
                // At this point, turn 1 would have 2 messages (User + Agent)
                const currentSession = this.chatState.getSessions().find(s => s.id === id);
                if (currentSession?.title === 'New Chat' || msgs.length <= 2) {
                  this.chatState.updateSessionTitle(id, event.data.conversationTitle);
                }
              }
            } else if (event.type === 'error') {
              this.chatState.removeLastMessage(id);
              this.handleError(new Error(event.error), 'Agent Error');
              return lastMsg;
            }

            return updated;
          });
        } catch (e) {
          console.error('Error parsing stream line', e);
        }
      }
    }
  }

  private handleError(err: any, contextMessage: string): void {
    console.error(contextMessage, err);
    const errorMessage = err?.message || err?.error || 'Unknown error';
    this.chatState.setError(`${contextMessage}: ${errorMessage}`);
  }
}
